import { FirebaseData } from "../../General/FirebaseData";
import { PropertyData } from "../PropertyData";
import { View } from "react-big-calendar";
import CollectionLoader from "../../General/CollectionLoader";
import BookingUnitSettingsLimitData from "./BookingUnitPropertyUnitData";
import { TranslationData } from "../../General/TranslationData";
import { CustomFieldDataType } from "../../Company/Collections/CustomFieldDataType";
import { SupportedLanguages } from "../../../../libs/i18next/language";
import { CustomFieldDataTypeHelper } from "../../Company/Collections/CustomFieldDataTypeHelper/CustomFieldDataTypeHelper";
import { CustomFieldDataOptions } from "../Collections/PropertyCustomFieldData";

export interface BookingUnitLockData {
  name: string;
  lockId: string;
}
export type BookingGrouping = "individually" | "unit";
export interface BookingDateRange {
  type?: string | null;
  dateTypeStartTime?: string | null;
  dateTypeEndTime?: string | null;
  stepTypeDuration?: number | null;
}
export interface BookingLimit {
  range: string;
  unit: string;
  count: number;
}
export interface BookingUnitCustomField {
  dataType: CustomFieldDataTypeHelper<any>;
  dataOptions: CustomFieldDataOptions;
  name: TranslationData;
  id: string;
}

export type BookingUnitPrivacy = "public" | "private";

export default class BookingUnitData extends FirebaseData {
  name: string = "";

  titleDisabled: boolean = false;
  emailRequired: boolean = false;
  phoneRequired: boolean = false;
  nameRequired: boolean = false;

  verificationText: string = "";

  verifiedBookingText: string = "";
  verifiedBookingTextDigitalKeys: string = "";

  dateRange?: BookingDateRange;

  emailVerificationReceivers: string[] = [];

  calendarColor?: string;
  defaultCalendarView?: View;

  digitalLocks: BookingUnitLockData[] = [];
  digitalKeyStartBufferTime?: number;
  digitalKeyEndBufferTime?: number;

  bookingPrivacy: BookingUnitPrivacy = "private";

  // Rules
  maxUnlocKeys?: number;

  allowOverlappingEvents: boolean = false;

  minBookingDuration?: number;
  maxBookingDuration?: number;

  minTimeToStart?: number;
  maxTimeToStart?: number;

  minTimeBetweenAllEvents?: number;
  minTimeBetweenGroupEvents?: number;

  minPeriodBeforeCancellation?: number;

  bookingGrouping: BookingGrouping = "individually";

  disabledParallelBookingUnits: BookingUnitData[] = [];
  disabledParallelBookingUnitsForOwner: string[] = [];

  bookingLimitEnabled: boolean = false;
  standardBookingLimit?: BookingLimit = undefined;
  rolloverEnabled: boolean = false;
  rolloverMultiplier?: number;

  propertyUnitSettings: CollectionLoader<BookingUnitSettingsLimitData>;

  standardLangCode: SupportedLanguages = SupportedLanguages.EN;
  customFields: BookingUnitCustomField[] = [];
  customFieldsOrder: string[] = [];

  constructor(id: string, collection: string, parent?: FirebaseData) {
    super(id, collection, parent);
    this.propertyUnitSettings = new CollectionLoader("propertyUnitSettings", this, BookingUnitSettingsLimitData);
  }

  setup(data: { [key: string]: any }) {
    this.name = typeof data.name === "string" ? data.name : "";

    this.maxUnlocKeys = typeof data.maxUnlocKeys === "number" ? data.maxUnlocKeys : undefined;
    this.verificationText = typeof data.verificationText === "string" ? data.verificationText : "";
    this.verifiedBookingText = typeof data.verifiedBookingText === "string" ? data.verifiedBookingText : "";
    this.verifiedBookingTextDigitalKeys = typeof data.verifiedBookingTextDigitalKeys === "string" ? data.verifiedBookingTextDigitalKeys : "";
    this.emailRequired = typeof data.emailRequired === "boolean" ? data.emailRequired : false;
    this.phoneRequired = typeof data.phoneRequired === "boolean" ? data.phoneRequired : false;
    this.nameRequired = typeof data.nameRequired === "boolean" ? data.nameRequired : false;
    this.titleDisabled = typeof data.titleDisabled === "boolean" ? data.titleDisabled : false;

    this.dateRange = typeof data.dateRange === "object" ? data.dateRange : undefined;

    this.digitalLocks = Array.isArray(data.digitalLocks) ? data.digitalLocks : [];
    this.digitalKeyStartBufferTime = typeof data.digitalKeyStartBufferTime === "number" ? data.digitalKeyStartBufferTime : undefined;
    this.digitalKeyEndBufferTime = typeof data.digitalKeyEndBufferTime === "number" ? data.digitalKeyEndBufferTime : undefined;

    this.bookingPrivacy =
      typeof data.bookingPrivacy === "string" && ["private", "public"].includes(data.bookingPrivacy) ? (data.bookingPrivacy as any) : "private";

    this.emailVerificationReceivers = Array.isArray(data.emailVerificationReceivers) ? data.emailVerificationReceivers : [];
    this.allowOverlappingEvents = typeof data.allowOverlappingEvents === "boolean" ? data.allowOverlappingEvents : false;
    this.minBookingDuration = typeof data.minBookingDuration === "number" ? data.minBookingDuration : undefined;
    this.maxBookingDuration = typeof data.maxBookingDuration === "number" ? data.maxBookingDuration : undefined;
    this.minTimeToStart = typeof data.minTimeToStart === "number" ? data.minTimeToStart : undefined;
    this.maxTimeToStart = typeof data.maxTimeToStart === "number" ? data.maxTimeToStart : undefined;
    this.minTimeBetweenAllEvents = typeof data.minTimeBetweenAllEvents === "number" ? data.minTimeBetweenAllEvents : undefined;
    this.minTimeBetweenGroupEvents = typeof data.minTimeBetweenGroupEvents === "number" ? data.minTimeBetweenGroupEvents : undefined;
    this.bookingGrouping = data.bookingGrouping === "unit" ? "unit" : "individually";
    this.minPeriodBeforeCancellation = typeof data.minPeriodBeforeCancellation === "number" ? data.minPeriodBeforeCancellation : undefined;
    this.calendarColor = typeof data.calendarColor === "string" ? data.calendarColor : undefined;
    this.defaultCalendarView = typeof data.defaultCalendarView === "string" ? (data.defaultCalendarView as View) : undefined;
    this.bookingLimitEnabled = typeof data.bookingLimitEnabled === "boolean" ? data.bookingLimitEnabled : false;
    this.rolloverEnabled = typeof data.rolloverEnabled === "boolean" ? data.rolloverEnabled : false;
    this.standardBookingLimit = typeof data.standardBookingLimit === "object" ? data.standardBookingLimit : undefined;
    this.rolloverMultiplier = typeof data.rolloverMultiplier === "number" ? data.rolloverMultiplier : undefined;

    this.disabledParallelBookingUnits = [];
    if (Array.isArray(data.disabledParallelBookingUnits) && this.parent instanceof PropertyData) {
      for (const id of data.disabledParallelBookingUnits) {
        const unit = this.parent.bookingUnits.addData(id);
        this.disabledParallelBookingUnits.push(unit);
      }
    }
    this.disabledParallelBookingUnitsForOwner = Array.isArray(data.disabledParallelBookingUnitsForOwner)
      ? data.disabledParallelBookingUnitsForOwner
      : [];

    this.standardLangCode = SupportedLanguages.getLanguageWithCode(data.standardLangCode);
    this.customFieldsOrder = Array.isArray(data.customFieldsOrder) ? data.customFieldsOrder : [];

    this.customFields = [];
    if (typeof data.customFields === "object") {
      for (const key of Object.keys(data.customFields)) {
        const name = new TranslationData();
        name.setup(data.customFields[key].name, this.standardLangCode);
        this.customFields.push({
          id: key,
          dataType: CustomFieldDataType.getTypeWithId(data.customFields[key].dataType),
          dataOptions: data.customFields[key].dataOptions,
          name: name,
        });
      }
    }

    super.setup(data);
  }
}
